// components/PostsPage/PostCard.tsx
import { Image, BarChart2, FileText } from "lucide-react";
import { X } from "@/components/logos/X";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMemo, useState } from "react";
import TwitterMediaGrid from "@/components/twitter/TwitterMedia";
import { formatDate, processPostData } from "@/components/twitter/twitterUtils";
import { TwitterAvatar } from "@/components/twitter/TwitterAvatar";
import { apiUrl } from "@/api/api";
import type { Persona } from "@/api/personas";
import type { Post } from "@/api/posts";

interface PostCardProps {
  post: Post;
  personaData: Persona;
}

const PostCard: React.FC<PostCardProps> = ({ post, personaData }) => {
  // Process the data for display
  const processedData = useMemo(() => processPostData(post), [post]);

  // State for expanded content
  const [isExpanded, setIsExpanded] = useState(false);

  // Open post in new tab
  const openPostInNewTab = () => {
    if (processedData.postId && personaData?.twitter) {
      window.open(
        `https://twitter.com/${personaData.twitter}/status/${processedData.postId}`,
        "_blank",
      );
    }
  };

  // Get badge color and text based on post type
  const getBadgeInfo = () => {
    switch (post.message_type) {
      case "media_post":
        return {
          text: "Media Post",
          className: "bg-amber-500/10 text-amber-500",
          icon: <Image className="h-3.5 w-3.5 mr-1" />,
        };
      case "media_evaluation":
        return {
          text: "Media Repost",
          className: "bg-blue-500/10 text-blue-500",
          icon: <BarChart2 className="h-3.5 w-3.5 mr-1" />,
        };
      case "post":
        return {
          text: "Regular Post",
          className: "bg-green-500/10 text-green-500",
          icon: <FileText className="h-3.5 w-3.5 mr-1" />,
        };
      default:
        return {
          text: "Post",
          className: "bg-gray-500/10 text-gray-500",
          icon: null,
        };
    }
  };

  // Get properly formatted media URLs
  const getMediaUrls = () => {
    // Check for media_urls array
    if (post.media_urls && post.media_urls.length > 0) {
      return post.media_urls;
    }

    // Check for GCS URL in metadata
    if (post.metadata?.gcs_url) {
      return [
        `${apiUrl}/get-file?url=${encodeURIComponent(post.metadata.gcs_url)}`,
      ];
    }

    // Check for best_media_url in metadata
    if (post.metadata?.best_media_url) {
      return [post.metadata.best_media_url];
    }

    return [];
  };

  const badgeInfo = getBadgeInfo();
  const mediaUrls = getMediaUrls();

  // Check if personaData is valid
  const validPersonaData =
    personaData && typeof personaData === "object" ? personaData : null;

  return (
    <div className="bg-background border rounded-2xl">
      {/* Badge, timestamp and Twitter logo */}
      <div className="px-6 pt-4 pb-2 flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={badgeInfo.className}>
            <span className="flex items-center">
              {badgeInfo.icon}
              {badgeInfo.text}
            </span>
          </Badge>
          <span className="text-sm text-muted-foreground">
            {processedData?.timestamp
              ? formatDate(processedData.timestamp)
              : "Unknown date"}
          </span>
        </div>
        <X width="16" height="16" fill="white" />
      </div>

      {/* Post user info */}
      <div className="px-6 pb-2">
        <div className="flex items-center gap-2 text-sm">
          {validPersonaData ? (
            <TwitterAvatar persona={validPersonaData} />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-200"></div>
          )}
        </div>
      </div>

      {/* Post content */}
      <div className="px-6 py-2">
        <p
          className={`text-sm ${!isExpanded && processedData.postText && processedData.postText.length > 280 ? "line-clamp-4" : ""}`}
        >
          {processedData?.postText || "No post text available"}
        </p>

        {processedData.postText && processedData.postText.length > 280 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-1 h-6 text-xs"
          >
            {isExpanded ? "Show less" : "Show more"}
          </Button>
        )}
      </div>

      {/* Media content if available */}
      {mediaUrls && mediaUrls.length > 0 && (
        <div className="px-6 py-2">
          <TwitterMediaGrid urls={mediaUrls} />
        </div>
      )}

      {/* Footer with view button */}
      <div className="px-6 py-3 flex justify-between items-center border-t border-border mt-2">
        <div className="text-sm text-muted-foreground">
          {/* Engagement metrics commented out as requested */}
          {/* <span>{processedData.metrics?.likes || 0} likes</span>
          <span className="ml-3">{processedData.metrics?.retweets || 0} retweets</span>
          <span className="ml-3">{processedData.metrics?.replies || 0} replies</span> */}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={openPostInNewTab}
          disabled={!processedData?.postId}
        >
          VIEW ON TWITTER
        </Button>
      </div>
    </div>
  );
};

export default PostCard;
